import './contact-us.css';
import EmailService from '../../../services/emailService';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { IContactUsForm } from './interfaces/IContactUsForm';
import { AppDispatch, RootState } from '../../../state/store';
import { useDispatch, useSelector } from 'react-redux';
import { hideLoadingOverlay, showLoadingOverlay } from '../../../state/slices/loadingOverlaySlice';
import { InlineWidget, PopupWidget } from 'react-calendly';
import { FormValidationService } from '../../../services/formValidationService';

const ContactUsComponent = () => {
  const phrases = useSelector((state: RootState) => state.phrases.value);
  const dispatch: AppDispatch = useDispatch<AppDispatch>();
  const form = useRef<any>();
  const firstNameInputRef = useRef<any>();
  const lastNameInputRef = useRef<any>();
  const emailInputRef = useRef<any>();
  const topicDropdownRef = useRef<any>();
  const messageInputRef = useRef<any>();
  const symbolsLeftRef = useRef<any>();

  const FIRST_NAME_MIN_LENGTH = 3;
  const FIRST_NAME_MAX_LENGTH = 75;
  const LAST_NAME_MIN_LENGTH = 3;
  const LAST_NAME_MAX_LENGTH = 75;
  const MESSAGE_MAX_LENGTH = 750;

  const defaultFormValue: IContactUsForm = {
    firstName: "",
    lastName: "",
    email: "",
    topic: "",
    message: ""
  }

  const [formState, setFormState] = useState<IContactUsForm>(defaultFormValue);
  const [messageSymbolsLeftState, setMessageSymbolsLeftState] = useState<number>(MESSAGE_MAX_LENGTH);

  const dropdownOptions = [
    {label: "New product development", value: "New product development"},
    {label: "Support for existing product", value: "Support for existing product"},
    {label: "Consultation", value: "Consultation"},
    {label: "Internship", value: "Internship"},
    {label: "Bug report", value: "Bug report"},
  ];

  /**
   * Taking care of error message visualization or removing it from the form.
   * @param {string} containerSelector CSS selector for DIV container with error messages.
   * @param {Array<string>} errorMessages Collection of error messages.
   */
  function afterFieldValidationEnd(containerSelector: string, errorMessages: Array<string>): void {
    if (errorMessages.length > 0) {
      FormValidationService.addErrorMessages(containerSelector, errorMessages);
    } else {
      FormValidationService.clearErrorMessages(containerSelector);
    }
  }

  function handleTopicChange(event: any): void {
    const topic: string = event.target.value;

    setFormState((previous: IContactUsForm) => {
      return {
        ...previous,
        topic: topic
      };
    });

    if(FormValidationService.validateNotEmpty(topic)) {
      afterFieldValidationEnd('#topic-error-messages', []);
    }
  }

  function handleMessageChange(event: any): void {
    const message: string = event.target.value;

    // Remove red border if it's applied.
    if (message !== "" && event.target.classList.contains('message-is-required')) {
      event.target.classList.remove('message-is-required');
    }

    setFormState((previous: IContactUsForm) => {
      return {
        ...previous, 
        message: message
      };
    });
    
    setMessageSymbolsLeftState((previous: number) => {
      const newValue = MESSAGE_MAX_LENGTH - message.length;
      
      if (newValue < 0) {
        symbolsLeftRef.current.classList.add('error-message');
      } else {
        symbolsLeftRef.current.classList.remove('error-message');
      }

      return newValue;
    });
  }

  function handleInputBottomMargin(inputRef: any, isValid: boolean) {
    if(isValid) {
      inputRef.current.classList.remove('mb-5');
      inputRef.current.classList.add('mb-15');
    } else {
      inputRef.current.classList.remove('mb-15');
      inputRef.current.classList.add('mb-5');
    }
  }

  function validateFirstName(): Array<string> {
    const firstNameErrorMessages: Array<string> = [];
    const isFirstNameNotEmpty: boolean = FormValidationService.validateNotEmpty(formState.firstName);

    if (!isFirstNameNotEmpty) {
      firstNameErrorMessages.push(`First name field is required.`);
    } else {
      const isFirstNameMinLengthCompatible: boolean = FormValidationService.validateMinLength(formState.firstName, FIRST_NAME_MIN_LENGTH);
      if (!isFirstNameMinLengthCompatible) {
        firstNameErrorMessages.push(`First name length can not be less than [number] symbols.`.replace('[number]', `${FIRST_NAME_MIN_LENGTH}`));
      }

      const isFirstNameMaxLengthCompatible: boolean = FormValidationService.validateMaxLength(formState.firstName, FIRST_NAME_MAX_LENGTH);
      if (!isFirstNameMaxLengthCompatible) {
        firstNameErrorMessages.push(`First name length can not exceed [number] symbols.`.replace('[number]', `${FIRST_NAME_MAX_LENGTH}`));
      }
    }

    handleInputBottomMargin(firstNameInputRef, firstNameErrorMessages.length === 0);
    afterFieldValidationEnd('#first-name-error-messages', firstNameErrorMessages);

    return firstNameErrorMessages;
  }

  function validateLastName(): Array<string> {
    const lastNameErrorMessages: Array<string> = [];
    const isLastNameNotEmpty: boolean = FormValidationService.validateNotEmpty(formState.lastName);
    if(!isLastNameNotEmpty) {
      lastNameErrorMessages.push(`Last name field is required.`);
    } else {
      const isLastNameMinLengthCompatible: boolean = FormValidationService.validateMinLength(formState.lastName, LAST_NAME_MIN_LENGTH);
      if (!isLastNameMinLengthCompatible) {
        lastNameErrorMessages.push(`Last name length can not be less than [number] symbols.`.replace('[number]', `${LAST_NAME_MIN_LENGTH}`));
      }

      const isLastNameMaxLengthCompatible: boolean = FormValidationService.validateMaxLength(formState.lastName, LAST_NAME_MAX_LENGTH);
      if (!isLastNameMaxLengthCompatible) {
        lastNameErrorMessages.push(`Last name length can not exceed [number] symbols.`.replace('[number]', `${LAST_NAME_MAX_LENGTH}`));
      }
    }

    handleInputBottomMargin(lastNameInputRef, lastNameErrorMessages.length === 0);
    afterFieldValidationEnd('#last-name-error-messages', lastNameErrorMessages);
    return lastNameErrorMessages;
  }

  function validateEmail(): Array<string> {
    const emailErrorMessages: Array<string> = FormValidationService.validateEmail(formState.email);
    handleInputBottomMargin(emailInputRef, emailErrorMessages.length === 0);
    afterFieldValidationEnd('#email-error-messages', emailErrorMessages);
    return emailErrorMessages;
  }

  function validateTopic(): Array<string> {
    const topicDropdownErrorMessages: Array<string> = [];
    const isTopicSelected = FormValidationService.validateNotEmpty(formState.topic);
    
    if(!isTopicSelected) {
      topicDropdownErrorMessages.push('Please select topic.');
    }
    
    afterFieldValidationEnd('#topic-error-messages', topicDropdownErrorMessages);
    return topicDropdownErrorMessages;
  }

  function validateMessage(): boolean {
    const isMessageProvided = FormValidationService.validateNotEmpty(formState.message);
    if (!isMessageProvided) {
      messageInputRef.current.classList.add('message-is-required');
    }
    // Invalid lenght is already shown in red. No need to do anything if invalid.
    const isMessageLengthValid = messageSymbolsLeftState >= 0;

    return isMessageProvided && isMessageLengthValid;
  }

  function validateForm(): boolean {
    const firstNameErrorMessages: Array<string> = validateFirstName();
    const lastNameErrorMessages: Array<string> = validateLastName();
    const emailErrorMessages: Array<string> = validateEmail();
    const topicDropdownErrorMessages: Array<string> = validateTopic();
    const isMessageValid = validateMessage();
    
    return  firstNameErrorMessages.length === 0 && 
            lastNameErrorMessages.length === 0 && 
            emailErrorMessages.length === 0 && 
            topicDropdownErrorMessages.length === 0 && 
            isMessageValid;
  }

  async function sendEmail(event: FormEvent): Promise<void> {
    event.preventDefault();
    const isFormValid: boolean = validateForm();
    
    if(!isFormValid) {
      return;
    }

    dispatch(showLoadingOverlay("Sending"));
    try {
      const response = await EmailService.sendEmail(form.current, false);
      if (response.status === 200) {
        setFormState(defaultFormValue);
      } else {
      // TODO: Use toast for this type of messages.
      console.error('Unexpected error occured. Please try again later.');
    }
    } catch (error) {
      // TODO: Use toast for this type of messages.
      console.error('Unexpected error occured. Please try again later.');
    }
    
    dispatch(hideLoadingOverlay());
  };

  /**
   * Gets hidden Calendly button.
   * @returns {HTMLDivElement} Calendly's button which opens the pop-up for scheduling a meeting.
   */
  function getCalendlyBadgeWidget(): HTMLDivElement {
    return document.querySelector('.calendly-badge-widget') as HTMLDivElement;
  }

  /**
   * Programmatically opens the pop-up for scheduling a meeting by clicking the hidden non-customizable Calendly button. 
   * @param event Click event over customized button.
   * @returns {void} void.
   */
  function openCalendlyWidget(event: any): void {
    event.preventDefault();
    getCalendlyBadgeWidget().click();
  }

  useEffect(() => {
    // Hide Calendly library's button which is not customizable when page loads.
    const calendlyWidget: HTMLDivElement = getCalendlyBadgeWidget();
    calendlyWidget.style.display = `none`;

    document.title = phrases[35];
  }, [phrases])

  return <>
    <h1 className='contact-us__heading'>{phrases[9]}</h1>
    <div className='contact-us__display-message-container d-flex'>
      <p className='contact-us__display-message'>
        {phrases[10]} <span className='contact-us__schedule-button' onClick={(e) => openCalendlyWidget(e)}>{phrases[30]}</span> {phrases[31]}</p>
    </div>
    <PopupWidget
      url="https://calendly.com/mariyan87/30min?hide_gdpr_banner=1"
      /*
       * react-calendly uses React's Portal feature (https://reactjs.org/docs/portals.html) to render the popup modal. As a result, you'll need to
       * specify the rootElement property to ensure that the modal is inserted into the correct domNode.
       */
      rootElement={document.getElementById("root")!}
      text="тук"
      textColor="#ffffff"
      color="#00a2ff"
    />
    <section className='contact-us__section-container'>
      <form ref={form} onSubmit={(event: FormEvent) => sendEmail(event)} className='contact-us__form-container'>
      <div>
        <label htmlFor="first-name" className='d-block required'>{phrases[12]}</label>
        <InputText 
          type="text" 
          id="first-name"
          name="from_first-name"
          className='p-inputtext-sm mb-15'
          placeholder={phrases[16]}
          value={formState.firstName}
          ref={firstNameInputRef}
          onChange={(event: any) => setFormState((previous: IContactUsForm) => {
            return {...previous, firstName: event.target.value as string};
          })}
        />
        <div id="first-name-error-messages" className="validation-errors"></div>
      </div>
      <div>
        <label htmlFor="last-name" className='d-block required'>{phrases[13]}</label>
        <InputText
          type="text"
          id="last-name"
          name="from_last-name"
          className='p-inputtext-sm mb-15'
          placeholder={phrases[17]}
          value={formState.lastName}
          ref={lastNameInputRef}
          onChange={(event: any) => setFormState((previous: IContactUsForm) => {
            return {...previous, lastName: event.target.value as string};
          })}
        />
        <div id="last-name-error-messages" className="validation-errors"></div>
      </div>
      <div>
        <label htmlFor="email" className='d-block required'>{phrases[8]}</label>
        <InputText
          type="text"
          id="email"
          name="from_email"
          className='p-inputtext-sm mb-15'
          placeholder={phrases[18]}
          value={formState.email}
          ref={emailInputRef}
          onChange={(event: any) => setFormState((previous: IContactUsForm) => {
            return {...previous, email: event.target.value as string};
          })}
        />
        <div id="email-error-messages" className="validation-errors"></div>
      </div>
      <div className='contact-us__topic-container'>
        <label htmlFor='topic' className='d-block required'>{phrases[11]}</label>
        <Dropdown 
          value={formState.topic}
          onChange={(event: DropdownChangeEvent) => handleTopicChange(event)}
          options={dropdownOptions}
          optionLabel="label"
          placeholder="Select topic"
          className="w-full md:w-14rem"
          name="topic"
          id="topic"
          ref={topicDropdownRef}
        />
        <div id="topic-error-messages" className="validation-errors"></div>
      </div>
      <div>
        <label htmlFor="message" className='d-block required'>{phrases[14]}</label>
        <InputTextarea
          name="message"
          autoResize
          rows={5}
          cols={45}
          value={formState.message}
          ref={messageInputRef}
          onChange={(event: any) => handleMessageChange(event)}
        />
        <div id="symbols-left" ref={symbolsLeftRef} className={'mb-10'}>Symbols left: {messageSymbolsLeftState}</div>
      </div>
      <Button type="submit" label={phrases[15]} unstyled={false} className='contact-us__send-button' />
      </form>
    </section>
    <div id="calendly-embed"></div>
    {/* <InlineWidget url={'https://calendly.com/mariyan87/30min?hide_gdpr_banner=1'} /> */}
  </>
}

export default ContactUsComponent
